// Blood flow › 3D flow, the panel's sections and the Methods section (features/cfd.ts drives the viewport).
// - Wall: the field the lumen glows with (shear now, its cycle mean, the oscillatory index, pressure), over the
//   engine's scale as the film draws it: cyan light added to the red membrane.
// - Tracers: the recorded tracers on or off, and how many of the run's drawn 20,000.
// A line under each says what the viewport is doing with the run: reading its cycle, on, or why not.

import { useAtlas } from '../app/store.ts';
import { Section, Segmented, Slider, Switch } from '../ui/primitives.tsx';
import { formatValue } from '../ui/chart.ts';
import { CFD_FULL_RUN, CFD_RUN } from '../scenarios/index.ts';
import { CFD_METHODS, DEFAULT_TRACERS, TRACERS_MAX, TRACERS_STEP, WALLS, cfdActive, shearGradient, useCfd, useCfdDrive, wallOf, type WallField } from './cfd.ts';
import './web.css';

const WALL_OPTIONS = WALLS.map((w) => ({ value: w.id, label: w.short }));

function statusLine(status: string, why: string | null, frames: number | undefined): string {
  if (status === 'loading') return 'Reading the run: its wall first, then the frames the clock needs, each kept once…';
  if (status === 'unsupported' || status === 'error') return why ?? 'The viewport refused the run.';
  if (status === 'on') return `${frames ? `${frames} frames a cycle; ` : ''}the frames the clock needs are read first, each kept once.`;
  return '';
}

/** The Wall and Tracers sections, while the view is on. The drive hook runs here, as the panel stays mounted. */
export function CfdSections() {
  useCfdDrive();
  const active = useAtlas(cfdActive);
  const wall = useCfd((s) => s.wall);
  const tracers = useCfd((s) => s.tracers);
  const tracersOn = useCfd((s) => s.tracersOn);
  const status = useCfd((s) => s.status);
  const why = useCfd((s) => s.why);
  const ranges = useCfd((s) => s.ranges);
  const header = useCfd((s) => s.header);
  const setWall = useCfd((s) => s.setWall);
  const setTracers = useCfd((s) => s.setTracers);
  const setTracersOn = useCfd((s) => s.setTracersOn);
  if (!active) return null;
  const w = wallOf(wall);
  const range = ranges[wall];
  const unit = w.units ? ` ${w.units}` : '';
  const line = statusLine(status, why, header?.time?.frames);
  return (
    <>
      <Section title="Wall">
        <div className="rows">
          <div className="row">
            <span>Field</span>
            <Segmented size="sm" label="Light the lumen wall by" value={wall} options={WALL_OPTIONS} onChange={(v: WallField) => setWall(v)} />
          </div>
        </div>
        <div className="colorbar cfd-legend" role="img" aria-label={`${w.name}${range ? `, ${formatValue(range[0])} to ${formatValue(range[1])}${unit}` : ''}, as light over the wall`}>
          <span className="name ba-type-small">{w.name}</span>
          <span className="strip" style={{ background: shearGradient }} />
          <span className="ticks ba-type-caption">
            <span>{range ? `${formatValue(range[0])}${unit}` : 'low'}</span>
            <span>{range ? `${formatValue(range[1])}${unit}` : 'high'}</span>
          </span>
        </div>
        <p className="live-hint">
          {w.perFrame
            ? 'The shear on the wall at this moment of the cycle, smoothed over the mesh for display; a low field leaves the plain red membrane.'
            : `${w.name}, one field for the whole cycle, drawn the same way.`}
        </p>
      </Section>
      <Section title="Tracers">
        <div className="rows">
          <label className="row">
            <span>Tracers</span>
            <Switch checked={tracersOn} onChange={setTracersOn} label="Tracers" />
          </label>
          {tracersOn ? (
            <div className="row live-position">
              <span>Count</span>
              <Slider label="Tracers drawn" min={TRACERS_STEP} max={TRACERS_MAX} step={TRACERS_STEP} value={tracers} onChange={setTracers} />
              <span className="num">{tracers.toLocaleString()}</span>
            </div>
          ) : null}
        </div>
        <p className="live-hint">
          {`Recorded tracers on their recorded paths, coloured by speed (the scale tops at 1.3 × the speed 95 % of them stay under at peak inflow), warm at systole; ${DEFAULT_TRACERS.toLocaleString()} of the run’s 100,000 are drawn at most.`}
        </p>
        {line ? (
          <p className={`live-hint cfd-status ${status}`} aria-live="polite">
            {line}
          </p>
        ) : null}
      </Section>
    </>
  );
}

/** The Methods sheet's section for the view: the run, its model and lattice, its cycle, what's drawn, its validation. */
export function CfdMethods() {
  const header = useCfd((s) => s.header);
  const wall = useCfd((s) => s.wall);
  const tracers = useCfd((s) => s.tracers);
  const tracersOn = useCfd((s) => s.tracersOn);
  const ranges = useCfd((s) => s.ranges);
  const w = wallOf(wall);
  const range = ranges[wall];
  if (!header) return <p className="muted">Loading the run’s header…</p>;
  const lat = header.model?.lattice;
  const t = header.time;
  const d = header.display;
  return (
    <section className="methods-sim methods-cfd">
      <h3>3D flow · lattice Boltzmann</h3>
      {header.methods && header.methods !== CFD_METHODS ? <p>{header.methods}</p> : null}
      <dl>
        <dt>Run</dt>
        <dd>
          <span className="mono">{CFD_RUN}</span> {header.version ? <span className="muted mono">{header.version}</span> : null} <span className="muted">{header.provenance ?? 'simulated'}</span>
        </dd>
        <dt>Model</dt>
        <dd>
          {header.model?.name ?? 'lattice Boltzmann'}
          {header.model?.version ? <span className="muted mono">{` ${header.model.version.slice(0, 8)}`}</span> : null}
          {header.model?.equations ? ` · ${header.model.equations}` : ''}
        </dd>
        {lat ? (
          <>
            <dt>Lattice</dt>
            <dd>{`${lat.dx_mm ?? '?'} mm cells${lat.nodes ? `, ${lat.nodes.toLocaleString()} fluid nodes` : ''}${lat.dt_s ? `, ${formatValue(lat.dt_s * 1e6)} µs steps` : ''}`}</dd>
          </>
        ) : null}
        {header.model?.outlets ? (
          <>
            <dt>Boundaries</dt>
            <dd>{header.model.outlets}</dd>
          </>
        ) : null}
        {t ? (
          <>
            <dt>Cycle</dt>
            <dd>{`${t.period_s ?? 1} s, ${t.frames ?? '?'} frames${t.dt_s ? ` ${formatValue(t.dt_s * 1000)} ms apart` : ''}; the frames the clock needs are read first, each kept once`}</dd>
          </>
        ) : null}
        <dt>Drawn</dt>
        <dd>{`${tracersOn ? `${tracers.toLocaleString()} recorded tracers, coloured by speed, inside ` : ''}the lumen wall lit by ${w.name.toLowerCase()}${range ? ` over ${formatValue(range[0])}–${formatValue(range[1])}${w.units ? ` ${w.units}` : ''}` : ''}, each frame smoothed over the mesh (4 umbrella passes) as the film’s is; the pack’s arteries are hidden, the lumen standing in for them`}</dd>
        {d ? (
          <>
            <dt>Copy</dt>
            <dd>{`Display copy${d.version ? ` ${d.version}` : ''} of ${d.of?.id ?? CFD_FULL_RUN}${d.of?.header_sha256 ? ` (header ${d.of.header_sha256.slice(0, 8)}…)` : ''}: the drawn tracers, their speeds and the wall shear as u16, the wall and the static fields unchanged; the source keeps everything.`}</dd>
          </>
        ) : null}
        {header.model?.wss ? (
          <>
            <dt>Wall shear</dt>
            <dd>{header.model.wss}</dd>
          </>
        ) : null}
        {header.inputs?.length ? (
          <>
            <dt>Inputs</dt>
            <dd>{header.inputs.map((i) => `${i.what}${i.provenance ? ` (${i.provenance})` : ''}`).join(' · ')}</dd>
          </>
        ) : null}
        {header.validation?.summary ? (
          <>
            <dt>Validation</dt>
            <dd>{`${header.validation.summary}${header.validation.report ? ` · ${header.validation.report}` : ''}`}</dd>
          </>
        ) : null}
        {header.pipeline?.path ? (
          <>
            <dt>Pipeline</dt>
            <dd className="mono">{`${header.pipeline.path}${header.pipeline.commit ? ` @ ${header.pipeline.commit.slice(0, 8)}` : ''}`}</dd>
          </>
        ) : null}
        {header.model?.citation ? (
          <>
            <dt>Citation</dt>
            <dd>{header.model.citation}</dd>
          </>
        ) : null}
      </dl>
    </section>
  );
}
